import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import _ from "lodash";

import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import { Checkbox } from "src/components/input";
import Tooltip from "src/components/tooltip";
import { searchLearners } from "src/redux/actions/search";
import { UserGroup } from "src/types/users";
import LinesEllipsis from "react-lines-ellipsis";
import styles from "./filters.module.scss";

interface UserGroupsFilterProps {
  selectedGroups: UserGroup[];
  onChange: (groups: UserGroup[]) => void;
  className?: string;
}

export const UserGroupsFilter: React.FC<UserGroupsFilterProps> = ({
  selectedGroups,
  onChange,
  className,
}) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [showSelectedTooltip, setShowSelectedTooltip] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { loading, userGroups, currentPage, nextPage, hasMoreItems } =
    useSelector((state: any) => {
      const userGroupState = _.get(state, "search.learners");
      const loading = _.get(userGroupState, "loading");
      const pagination = _.get(userGroupState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");
      const currentPage = _.get(pagination, "currentPage", 0);
      const userGroups =
        (_.get(userGroupState, "items") || []).filter(
          (item: { entityType?: string }) => item.entityType === "user_group",
        );

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading,
        userGroups,
        currentPage,
        nextPage,
        hasMoreItems,
      };
    }, _.isEqual);

  const toggleLabel = useMemo(() => {
    const count = selectedGroups.length;

    if (count === 0) {
      return "all user groups";
    }

    if (count === 1) {
      return selectedGroups[0]?.name || "1 user group";
    }

    return `${count} user groups`;
  }, [selectedGroups]);

  const hasSelection = selectedGroups.length > 0;
  const singleSelected = selectedGroups.length === 1;
  const selectedGroupName = singleSelected
    ? selectedGroups[0]?.name || "1 user group"
    : "";

  const fetchUserGroups = (searchQuery: string, page: number) => {
    dispatch(
      searchLearners({
        query: searchQuery || "",
        page,
        limit: 20,
      }),
    );
  };

  useEffect(() => {
    if (open) {
      fetchUserGroups(query, 0);
    }
  }, [query, open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleGroup = (group: UserGroup) => {
    const exists = selectedGroups.some((item) => item.id === group.id);
  
    if (exists) {
      onChange(selectedGroups.filter((item) => item.id !== group.id));
    } else {
      onChange([...selectedGroups, group]);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([]);
    setOpen(false);
  };

  return (
    <div className={styles.filterDropdown} ref={ref}>
      <button
        type="button"
        className={cx(styles.toggle, {
          [styles.isOpen]: open,
          [styles.singleSelected]: singleSelected,
        })}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={styles.label}>
          {singleSelected ? (
            <Tooltip
              content={selectedGroupName}
              placement="top"
              disabled={!showSelectedTooltip}
            >
              <span className={styles.labelEllipsis}>
                <LinesEllipsis
                  text={selectedGroupName}
                  maxLine="1"
                  basedOn="letters"
                  onReflow={({ clamped }) => setShowSelectedTooltip(clamped)}
                />
              </span>
            </Tooltip>
          ) : (
            toggleLabel
          )}
        </span>
        {hasSelection && (
          <i
            className={cx("paperflite-close", styles.clearIcon)}
            onClick={handleClear}
          />
        )}
        {!hasSelection && (
          <i className={cx("paperflite-dropdown", styles.chevron)} />
        )}
      </button>

      {open && (
        <div className={cx(styles.userGroupsMenu, className)}>
          <div className={styles.menuInner}>
            <Actions.Search
              query={query}
              onSearch={(value) => setQuery(value || "")}
            />
            <InfiniteScroll
              className={styles.groupsList}
              loading={loading}
              hasMore={hasMoreItems}
              loadMore={() => {
                if (nextPage !== null) {
                  fetchUserGroups(query, nextPage);
                }
              }}
              loader={() =>
                _.times(
                  _.size(userGroups) === 0 || currentPage === 0 ? 2 : 1,
                  (index) => (
                    <div key={index} className={styles.groupPlaceholder} />
                  ),
                )
              }
            >
              {_.size(userGroups) === 0 && !loading && (
                <span className={styles.noResults}>No results found.</span>
              )}
              {userGroups.map((group: UserGroup) => {
                const checked = selectedGroups.some(
                  (item) => item.id === group.id,
                );

                return (
                  <div
                    key={group.id}
                    className={styles.groupRow}
                    onClick={() => handleToggleGroup(group)}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => handleToggleGroup(group)}
                    />
                    <span className={styles.groupName}>
                      {group.name}
                      <span className={styles.count}>
                        {" "}
                        ({group.usersCount ?? 0})
                      </span>
                    </span>
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
        </div>
      )}
    </div>
  );
};
